import { Alert, App, Button, Card, Form, Input, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { changeOwnPassword } from './identityApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface FormValues {
  currentPassword: string
  newPassword: string
  repeat: string
}

/**
 * O'Z parolini almashtirish — BUG-046 («PASSWORD RESET TUGMASI MAVJUD
 * EMAS»).
 *
 * <h2>Nima o'lchangan</h2>
 * 2026-08-16, jonli panel, `/settings` ekrani: tugmalar — «Reset
 * two-factor authentication», «Save», «Save». Parolga oid BITTA ham
 * tugma yo'q, profil menyusida esa faqat «Sign out». Ya'ni o'z
 * parolini almashtirishning YAGONA yo'li `/users` sahifasi edi, u esa
 * `user.update` huquqini talab qiladi — oddiy xodimda u yo'q.
 *
 * <h2>⚠️ TOTP CHETLAB O'TILMAYDI</h2>
 * Bu forma faqat ALLAQACHON kirgan odamda ko'rinadi, ya'ni TOTP
 * bosqichi o'tilgan. «Parolni unutdim» turidagi tokensiz tiklash
 * ATAYLAB qo'shilmadi (`ISH-NAVBATI.md`): u aynan TOTP'ni aylanib
 * o'tish kanaliga aylanardi.
 *
 * <h2>⚠️ JORIY PAROL SO'RALADI</h2>
 * Sabab `SelfPasswordRequest` javadoc'ida: o'g'irlangan sessiya
 * tokeni yolg'iz o'zi hisobni egallashga yetmasin.
 *
 * <h2>⚠️ Ikki marta kiritish MAJBURIY</h2>
 * `PasswordModal` dagi bilan bir xil sabab: parol ekranda ko'rinmaydi
 * va xato yozilgani faqat keyingi kirishda — kirib bo'lmaganda —
 * bilinardi.
 *
 * ⚠️ Uzunlik chegarasi <b>12…72</b> — serverdagi `SelfPasswordRequest`
 * bilan AYNAN bir xil. 72 esa `Bcrypt72PasswordEncoder` (CVE-2025-22228)
 * chegarasi: undan uzun parolning oxiri hisobga olinmaydi, ya'ni
 * ruxsat berish qalbaki xavfsizlik hissini berardi.
 */
export function SelfPasswordCard() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      changeOwnPassword(values.currentPassword, values.newPassword),
    onSuccess: () => {
      void message.success(t('identity.selfPassword.done'))
      form.resetFields()
    },
    // ⚠️ `onError` ATAYLAB yo'q — xato AYNAN shu kartada, quyidagi
    // banner sifatida qoladi. `message.error` o'z-o'zidan yo'qoladi va
    // "joriy parol noto'g'ri" degan aniq sabab ko'rilmay qolardi.
  })

  return (
    <Card title={t('identity.selfPassword.title')}>
      <Typography.Paragraph type="secondary">
        {t('identity.selfPassword.hint')}
      </Typography.Paragraph>

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" disabled={save.isPending}>
        <Form.Item
          name="currentPassword"
          label={t('identity.selfPassword.current')}
          rules={[{ required: true, message: t('identity.selfPassword.currentRequired') }]}
        >
          <Input.Password visibilityToggle={false} autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('identity.selfPassword.next')}
          extra={t('identity.create.passwordHint')}
          rules={[
            { required: true, message: t('identity.create.passwordRequired') },
            { min: 12, max: 72, message: t('identity.selfPassword.length') },
          ]}
        >
          <Input.Password visibilityToggle={false} autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="repeat"
          label={t('identity.password.repeat')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('identity.password.repeatRequired') },
            // antd validator'i `unknown` beradi — aniq tiplaymiz, aks holda
            // tip xavfsizligi shu yerda jimgina yo'qolardi
            ({ getFieldValue }) => ({
              validator: (_rule, value: unknown) => {
                const first: unknown = getFieldValue('newPassword')
                if (typeof value === 'string' && value === first) return Promise.resolve()
                return Promise.reject(new Error(t('identity.password.mismatch')))
              },
            }),
          ]}
        >
          <Input.Password visibilityToggle={false} autoComplete="new-password" />
        </Form.Item>

        <Button
          type="primary"
          loading={save.isPending}
          onClick={() => submitOnValid(form, (values) => save.mutate(values))}
        >
          {t('identity.selfPassword.submit')}
        </Button>
      </Form>
    </Card>
  )
}
